import { useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { BrandLogo } from "../brand/BrandLogo";
import { NAV, UTILITY_NAV } from "../../lib/nav";
import { HeaderThemeContext } from "./HeaderTheme";
import { isEn, langHref, t } from "../../lib/i18n";
import { Img } from "../motion/Reveal";

/**
 * Full-width brand shell: EQVA far left, five main entries centred, utilities (join, investor, EN) far right.
 * No share price up here – that belongs to the investor page. The shell deliberately does NOT follow the
 * content grid; the hero below sits in its own, narrower container. A single inset hairline (same outer
 * margin as the shell content, almost invisible) separates navigation from page.
 */
export const Header = () => {
  const { pathname } = useLocation();
  const { dark } = useContext(HeaderThemeContext);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [open, setOpen] = useState(false);
  const [dd, setDd] = useState(false);
  const [ddSeen, setDdSeen] = useState(false);
  const overHero = dark && !scrolled && !open;
  const atTop = !scrolled && !open && !dd;

  useEffect(() => {
    let last = window.scrollY, acc = 0;
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      // Auto-hide: slide away after a stretch of downward scrolling, return on the first nudge up.
      const dy = y - last; last = y;
      acc = Math.sign(dy) === Math.sign(acc) ? acc + dy : dy;
      if (y < 160) setHidden(false);
      else if (acc > 90) setHidden(true);
      else if (acc < -12) setHidden(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setOpen(false); setDd(false); }, [pathname]);
  useEffect(() => { if (dd) setDdSeen(true); }, [dd]);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { setDd(false); setOpen(false); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const linkCls = ({ isActive }) => `nav-link text-[14px] font-medium ${isActive ? "active" : ""}`;
  const omOssActive = pathname.startsWith("/om-oss");

  return (
    <header
      data-testid="site-header"
      data-over-hero={overHero ? "true" : "false"}
      data-hidden={hidden && !open && !dd ? "true" : "false"}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,color,transform] duration-300 ${
        overHero ? "bg-transparent text-white" : atTop ? "bg-white/0 text-ink" : "bg-white/[0.98] text-ink"
      } ${hidden && !open && !dd ? "-translate-y-full" : ""}`}
    >
      <div className={`header-shell relative grid grid-cols-[1fr_auto] lg:grid-cols-[1fr_auto_1fr] items-center gap-6 transition-[height] duration-300 ${scrolled && !open ? "h-[68px]" : "h-[var(--header-h)]"}`}>
        <Link to="/" data-testid="nav-brand-wordmark" aria-label={t("EQVA – til forsiden")} className="brand-mark relative justify-self-start" data-light={overHero ? "true" : "false"}>
          {/* both colourways stacked; the header's state picks one so the swap over the dark hero is a crossfade, not a reload */}
          <BrandLogo variant="color" height={34} className="brand-mark-color" alt="EQVA" aria-hidden={overHero ? "true" : undefined} />
          <BrandLogo variant="white" height={34} className="brand-mark-white absolute inset-0" alt="" aria-hidden="true" />
        </Link>

        <nav aria-label={t("Hovedmeny")} className="hidden lg:flex items-center justify-center gap-7 xl:gap-9">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.id} className="relative" onMouseEnter={() => setDd(true)} onMouseLeave={() => setDd(false)}>
                <button
                  data-testid="nav-dropdown-om-oss"
                  aria-haspopup="true"
                  aria-expanded={dd}
                  onClick={() => setDd((v) => !v)}
                  className={`nav-link flex items-center gap-1 text-[14px] font-medium ${omOssActive ? "active" : ""}`}
                >
                  {item.label} <ChevronDown size={14} className={`transition-transform duration-200 ${dd ? "rotate-180" : ""}`} />
                </button>
                <div className={`absolute left-1/2 -translate-x-1/2 top-full pt-5 transition-[opacity,transform] duration-200 ease-out ${dd ? "opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-1"}`}>
                  <div className="grid w-[680px] grid-cols-[1.35fr_1fr] bg-white text-ink border border-line">
                    <ul className="py-4">
                      {item.children.map((c) => (
                        <li key={c.href}>
                          <Link to={c.href} data-testid={`nav-sub-${c.href.split("/").pop()}`} onClick={() => setDd(false)}
                            className={`group block px-7 py-3 transition-colors duration-200 hover:bg-surface ${pathname === c.href ? "text-accent" : ""}`}>
                            <span className="flex items-center justify-between text-[15px] font-medium group-hover:text-blue">
                              {c.label}
                              <ArrowRight size={15} className="-translate-x-1 opacity-0 transition-[transform,opacity] duration-200 group-hover:translate-x-0 group-hover:opacity-100" />
                            </span>
                            <span className="mt-0.5 block text-[13px] text-muted">{c.desc}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                    <Link to="/om-oss" onClick={() => setDd(false)} className="group relative block overflow-hidden bg-navy text-white">
                      {ddSeen && <Img src="/images/om-oss-sunde.jpg" alt="" sizes="280px" fade={false} className="absolute inset-0 h-full w-full object-cover opacity-70 transition-transform duration-700 group-hover:scale-105" />}
                      <span className="absolute inset-0 bg-[linear-gradient(to_top,rgba(8,28,48,0.92),rgba(8,28,48,0.2))]" />
                      <span className="relative flex h-full flex-col justify-end p-6">
                        <span className="t-label text-white/70">{t("Om EQVA")}</span>
                        <span className="mt-2 text-[19px] font-medium leading-snug">{t("Lokalt ledet. Langsiktig eid.")}</span>
                        <span className="link-arrow mt-4 !text-white text-[14px]">{t("Dette er EQVA")} <ArrowRight size={15} /></span>
                      </span>
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.id} to={item.href} data-testid={`nav-link-${item.id}`} className={linkCls}>{item.label}</NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center justify-self-end gap-7 xl:gap-8">
          {UTILITY_NAV.map((u) => (
            <NavLink key={u.id} to={u.href} data-testid={`nav-link-${u.id}`} className={linkCls}>{u.label}</NavLink>
          ))}
          {/* language switch: the same page in the other language (full navigation – the language is fixed per page load) */}
          <a href={langHref(isEn ? "no" : "en")} data-testid="nav-link-english" className="nav-link text-[12px] font-semibold tracking-[0.08em]" lang={isEn ? "nb" : "en"} hrefLang={isEn ? "nb" : "en"} aria-label={isEn ? "Norsk" : "English"}>{isEn ? "NO" : "EN"}</a>
        </div>

        <button data-testid="mobile-menu-trigger" aria-label={open ? t("Lukk meny") : t("Åpne meny")} aria-expanded={open} onClick={() => setOpen((v) => !v)} className="lg:hidden justify-self-end p-2 -mr-2">
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>

        {/* Inset hairline – felt more than seen */}
        <span aria-hidden="true" className={`header-rule ${overHero ? "is-light" : ""} ${open ? "opacity-0" : ""}`} />
      </div>

      {/* Portaled to <body>: the header animates with transform, and a transformed ancestor would turn this
          position:fixed panel into a 1 px sliver pinned to the header box. */}
      {open && createPortal(
        <div data-testid="mobile-menu" className="lg:hidden fixed inset-x-0 bottom-0 top-[var(--header-h)] z-[45] bg-white text-ink border-t border-line overflow-y-auto overscroll-contain">
          <nav aria-label={t("Mobilmeny")} className="wrap flex min-h-full flex-col py-8">
            {NAV.map((item, i) => (
              <div key={item.id} className="menu-in border-b border-line" style={{ "--d": `${i * 45}ms` }}>
                <Link to={item.href} data-testid={`mobile-nav-${item.id}`} className="flex items-center justify-between py-4 text-[28px] font-medium tracking-[-0.02em]">
                  {item.label} <ArrowRight size={22} className="text-muted" />
                </Link>
                {item.children && (
                  <ul className="grid grid-cols-2 gap-x-4 pb-5 -mt-1">
                    {item.children.map((c) => (
                      <li key={c.href}><Link to={c.href} data-testid={`mobile-sub-${c.href.split("/").pop()}`} className="block py-2 text-[15px] text-muted">{c.label}</Link></li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
            <div className="menu-in mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" style={{ "--d": "260ms" }}>
              {UTILITY_NAV.map((u) => (
                <Link key={u.id} to={u.href} data-testid={`mobile-nav-${u.id}`} className="text-[17px] font-medium">{u.label}</Link>
              ))}
            </div>
            <div className="menu-in mt-auto pt-10 flex items-end justify-between t-small text-muted" style={{ "--d": "320ms" }}>
              <span>Sunde i Sunnhordland<br />office@eqva.no</span>
              <a href={langHref(isEn ? "no" : "en")} data-testid="mobile-nav-english" className="t-label text-ink" lang={isEn ? "nb" : "en"} hrefLang={isEn ? "nb" : "en"}>{isEn ? "Norsk" : "English"}</a>
            </div>
          </nav>
        </div>,
        document.body
      )}
    </header>
  );
};
